import React from 'react';
import { EditorState, Modifier, type EditorState as EditorStateType } from 'draft-js';
import { Button, DialogActions, DialogContent, Paper, Popover, TextField } from '@mui/material';

import useSettings from 'app/main/components/hooks/useSettings';
import editorDecorator from '../ComponentPlugins';

interface DialogAddLinkProps {
	/** The element ref used to show this dialog */
	anchorElement: HTMLButtonElement | null;
	/** True to show the popover component, false to hide it */
	open: boolean;
	/** Function used to close the dialog */
	handleClose: () => void;
	/** The HtmlEditor content state */
	editorState: EditorStateType;
	/** Function used to set the HtmlEditor content state */
	setEditorState: React.Dispatch<React.SetStateAction<EditorStateType>>;
	/** The current selected text in the editor */
	selectedText?: string;
}

/** This dialog is used to expand the HtmlEditor capabilities and add a way to insert working links */
export default function DialogAddLink({
	anchorElement,
	open,
	handleClose,
	editorState,
	setEditorState,
	selectedText
}: DialogAddLinkProps) {
	const { t } = useSettings();

	const [hyperLink, setHyperLink] = React.useState('');

	const handleAddLink = () => {
		const currentContent = editorState.getCurrentContent();

		// Creating Link entity
		currentContent.createEntity('LINK', 'MUTABLE', {
			url: !hyperLink.includes('http://') && !hyperLink.includes('https://') ? `http://${hyperLink}` : hyperLink,
			target: '_blank'
		});
		const entityKey = currentContent.getLastCreatedEntityKey();

		// Get the selected text it'll used to override the text
		const selection = editorState.getSelection();

		// Add new text or override the selection text that the user selects
		const textWithEntity = Modifier.replaceText(
			currentContent,
			selection,
			selectedText || hyperLink,
			editorState.getCurrentInlineStyle(),
			entityKey
		);

		setEditorState(EditorState.createWithContent(textWithEntity, editorDecorator));
		handleClose();
	};

	return (
		<Popover
			anchorEl={anchorElement}
			open={open}
			onClose={handleClose}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'left'
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'left'
			}}
		>
			<Paper>
				<DialogContent>
					<TextField
						label="Link"
						variant="filled"
						autoComplete="off"
						slotProps={{ inputLabel: { shrink: true } }}
						onBlur={e => setHyperLink(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>{t('DIALOG_ACTION_CANCEL')}</Button>
					<Button onClick={handleAddLink}>{t('DIALOG_ACTION_ADD')}</Button>
				</DialogActions>
			</Paper>
		</Popover>
	);
}
